import { Api } from '$api';
import { CurrencyEnum, UserModel } from '$store/models';

import {
  FanBreakdown,
  FanDailyRow,
  FanDetailData,
  FansListResult,
  FansQuery,
  FanRowData,
} from './StatisticsProFans.types';

const DAY_SECONDS = 86400;
const toUtcDayStart = (unix: number) => Math.floor(unix / DAY_SECONDS) * DAY_SECONDS;

interface RawFanRow {
  userId: number;
  totalAmount: number;
  totalCount: number;
  breakdown: FanBreakdown | null;
}
interface RawFansResponse {
  fans?: RawFanRow[];
  nextFrom?: string | null;
  users?: Record<string, UserModel> | null;
  summary?: { fanCount: number; totalAmount: number } | null;
  joined?: number | null;
}
interface RawFanDetailResponse {
  userId: number;
  currency: string;
  totalAmount: number;
  totalCount: number;
  breakdown: FanBreakdown | null;
  rows?: { date: number; earnings: FanBreakdown | null }[];
  user: UserModel;
  promoLink?: { id: number; name: string } | null;
}

export async function fetchFansList(query: FansQuery): Promise<FansListResult> {
  const data: RawFansResponse = await Api.get('/stats/pro/fans', {
    params: {
      from: toUtcDayStart(query.from),
      to: toUtcDayStart(query.to),
      currency: query.currency.toUpperCase(),
      sort: query.sort,
      order: query.order ?? 'desc',
      startFrom: query.startFrom || undefined,
      limit: query.limit ?? 30,
      promoLinkIds:
        query.promoLinkIds === 'all'
          ? 'all'
          : query.promoLinkIds?.length
          ? query.promoLinkIds.join(',')
          : undefined,
    },
  });

  const fans: FanRowData[] = (data?.fans ?? []).map((row) => ({
    userId: row.userId,
    totalAmount: row.totalAmount,
    totalCount: row.totalCount,
    breakdown: row.breakdown ?? {},
  }));

  return {
    fans,
    nextFrom: data?.nextFrom ?? null,
    users: (data?.users ?? {}) as Record<number, UserModel>,
    summary: data?.summary ?? undefined,
    joined: data?.joined ?? undefined,
  };
}

export async function fetchFanDetail(
  userId: number,
  query: FansQuery,
): Promise<FanDetailData> {
  const data: RawFanDetailResponse = await Api.get(`/stats/pro/fans/${userId}`, {
    params: {
      from: toUtcDayStart(query.from),
      to: toUtcDayStart(query.to),
      currency: query.currency.toUpperCase(),
    },
  });

  const rows: FanDailyRow[] = (data?.rows ?? [])
    .map((row) => ({ date: row.date, earnings: row.earnings ?? {} }))
    .sort((a, b) => a.date - b.date);

  return {
    userId: data.userId,
    currency: data.currency.toLowerCase() as CurrencyEnum,
    totalAmount: data.totalAmount,
    totalCount: data.totalCount,
    breakdown: data.breakdown ?? {},
    rows,
    user: data.user,
    promoLink: data.promoLink ?? undefined,
  };
}
